import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { formatDateWithDay } from "../../utils/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faEllipsisVertical,
  faLocationDot,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";

const EventDetails = ({ event, isEventExpired, refreshData }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [isSendingDriverReminder, setIsSendingDriverReminder] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const handleToggleRsvp = async () => {
    try {
      await fetch(`${apiBaseUrl}/api/events/${event.id}/toggle-rsvp`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("userToken")
            ? { Authorization: `Bearer ${localStorage.getItem("userToken")}` }
            : {}),
        },
        body: JSON.stringify({ rsvpOn: !event.rsvpOn }),
      });
      refreshData();
    } catch (err) {
      console.error("Failed to toggle RSVP", err);
    }
  };

  const handleSendReminder = async () => {
    setIsSendingReminder(true);
    setReminderStatus(null);
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/events/${event.id}/send-ride-reminder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(localStorage.getItem("userToken")
              ? { Authorization: `Bearer ${localStorage.getItem("userToken")}` }
              : {}),
          },
        },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Failed to send reminder.");
      }
      setReminderStatus({
        type: "success",
        message: `${result.message} ${
          result.failed ? `${result.failed} failed.` : ""
        } ${result.skipped ? `${result.skipped} skipped.` : ""}`.trim(),
      });
    } catch (err) {
      console.error("Failed to send reminder", err);
      setReminderStatus({
        type: "error",
        message: err.message || "Failed to send reminder.",
      });
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleSendDriverReminder = async () => {
    setIsSendingDriverReminder(true);
    setReminderStatus(null);
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/events/${event.id}/send-driver-reminder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(localStorage.getItem("userToken")
              ? { Authorization: `Bearer ${localStorage.getItem("userToken")}` }
              : {}),
          },
        },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Failed to send driver reminder.");
      }
      setReminderStatus({
        type: "success",
        message: `${result.message} ${
          result.failed ? `${result.failed} failed.` : ""
        } ${result.skipped ? `${result.skipped} skipped.` : ""}`.trim(),
      });
    } catch (err) {
      console.error("Failed to send driver reminder", err);
      setReminderStatus({
        type: "error",
        message: err.message || "Failed to send driver reminder.",
      });
    } finally {
      setIsSendingDriverReminder(false);
    }
  };

  const drawerRef = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [drawerRef]);

  if (!event) return "Loading...";

  const shouldShowReminderButtons =
    event.pingSchedule && new Date() > new Date(event.pingSchedule);
  const shouldShowSendReminder =
    event.pingParticipants && shouldShowReminderButtons;
  const shouldShowSendDriverReminder =
    event.pingDrivers && shouldShowReminderButtons;

  return (
    <div className="event-details bg-white shadow-lg overflow-visible rounded-xl relative">
      <div className="p-5 sm:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-md px-2.5 py-1 text-xs font-bold ${
                  isEventExpired
                    ? "bg-gray-100 text-gray-600"
                    : "bg-green-50 text-success"
                }`}
              >
                {isEventExpired ? "Past Event" : "Active Event"}
              </span>
              <span className="rounded-md bg-[#eaf3fa] px-2.5 py-1 text-xs font-bold text-[#004a7e]">
                {event.eventSignUpType}
              </span>
            </div>
            <h1 className="mt-3 text-2xl font-black leading-tight text-gray-900 sm:text-3xl">
              {event.title}
            </h1>
            <div className="mt-3 space-y-2 text-sm text-gray-600">
              <p className="flex items-start gap-2">
                <FontAwesomeIcon
                  icon={faCalendarDays}
                  className="mt-0.5 text-[#004a7e]"
                />
                <span>{formatDateWithDay(event.dateOfOccurrence)}</span>
              </p>
              {event.location && (
                <p className="flex items-start gap-2">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="mt-0.5 text-[#004a7e]"
                  />
                  <span>{event.location}</span>
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-end">
            <label className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
              <span>RSVP {event.rsvpOn ? "On" : "Off"}</span>
              <input
                type="checkbox"
                className="toggle toggle-success toggle-sm"
                checked={event.rsvpOn}
                onChange={handleToggleRsvp}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              {shouldShowSendReminder && (
                <button
                  onClick={handleSendReminder}
                  disabled={isSendingReminder}
                  className="rounded-lg bg-[#004a7e] px-3 py-2 text-sm font-bold text-white hover:bg-[#003a64]"
                >
                  {isSendingReminder ? "Sending..." : "Send Reminder"}
                </button>
              )}
              {shouldShowSendDriverReminder && (
                <button
                  onClick={handleSendDriverReminder}
                  disabled={isSendingDriverReminder}
                  className="rounded-lg bg-[#004a7e] px-3 py-2 text-sm font-bold text-white hover:bg-[#003a64]"
                >
                  {isSendingDriverReminder
                    ? "Sending..."
                    : "Send Driver Reminder"}
                </button>
              )}
              <button
                onClick={refreshData}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
                title="Refresh"
              >
                <FontAwesomeIcon icon={faRefresh} className="mr-2" />
                Refresh
              </button>
              <button
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faEllipsisVertical} className="mr-2" />
                More
              </button>
            </div>
            {reminderStatus && (
              <div
                className={`max-w-sm rounded-lg px-3 py-2 text-sm font-semibold ${
                  reminderStatus.type === "success"
                    ? "bg-green-50 text-green-700"
                    : "bg-red-50 text-red-700"
                }`}
              >
                {reminderStatus.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Side Drawer */}
      <div
        ref={drawerRef}
        className={`transform top-0 left-0 w-64 bg-white fixed h-full overflow-auto ease-in-out transition-all duration-300 z-50 shadow-xl ${
          isDrawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Close Button */}
        <div className="p-4 text-right">
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="text-gray-600 hover:text-gray-800"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        {/* Drawer Content */}
        <nav className="flex flex-col p-4 space-y-2">
          <Link
            to={`/event/edit/${event.id}`}
            className="p-3 hover:bg-blue-50 rounded-lg text-primary font-medium"
          >
            {isEventExpired ? "View Event Details" : "Edit Event"}
          </Link>
          <Link
            to={`/event/sms-template/${event.id}`}
            className="p-3 hover:bg-blue-50 rounded-lg text-primary font-medium"
          >
            SMS Templates
          </Link>
          <Link
            to={`/group/${event.participantGroup}`}
            className="p-3 hover:bg-blue-50 rounded-lg text-primary font-medium"
          >
            Manage Group
          </Link>
          <Link
            to={`/event-rsvp/${event.eventCode}`}
            className="p-3 hover:bg-blue-50 rounded-lg text-primary font-medium"
          >
            Public RSVP View
          </Link>
          {/* Add more links as needed */}
        </nav>
      </div>
    </div>
  );
};

export default EventDetails;
